import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import { Box, Typography } from '@mui/material';
import * as React from 'react';
import { memo } from 'react';
import GridLayout from 'react-grid-layout';
import { useTranslation } from 'react-i18next';

import { AddWorkspaceButton } from '~/scenes/MyWorkspace/components/ManageWorkspaceLayouts/components/AddWorkspaceButton';
import { PlaceholderWidget } from '~/scenes/MyWorkspace/components/ManageWorkspaceLayouts/components/PlaceholderWidget';
import { mergeSxProps } from '~/utils/helpers/sxProps';

import { WorkspaceType } from '../../data';
import { myWorkspaceWidgetsMap } from '../Widgets/data/widgetsMap';

import { AddWidgetMenu } from './components/AddWidgetMenu';
import { ManageWidgetMenu } from './components/ManageWidgetMenu';
import { useManageWorkspaceLayouts } from './useManageWorkspaceLayouts';
import { useSxStyles } from './useStyles';

export const ManageWorkspaceLayouts: React.FC = () => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const {
    activeWorkspace,
    containerRef,
    containerWidth,
    layoutSettings,
    layoutData,
    manageWorkspace,
    handleDragStop,
    handleResizeStop,
    hasNoWorkspace,
    workspaces,
    isWidgetAvailable,
  } = useManageWorkspaceLayouts();

  const movable = !!manageWorkspace && layoutSettings.canReorder;
  const resizable = !!manageWorkspace && layoutSettings.canResize;
  const numberOfColumns = layoutSettings.cols;
  const spacingBetweenWidgets = 16;

  const conditionalSx = {
    widgetContainer: mergeSxProps(
      sxStyles.widgetContainer,
      movable ? sxStyles.movableWidgetContainer : undefined,
    ),
    widget: mergeSxProps(
      sxStyles.widget,
      !!manageWorkspace ? sxStyles.editableWidget : undefined,
    ),
  };

  return (
    <Box ref={containerRef} data-cy="layout" sx={sxStyles.root}>
      {layoutData &&
        ((layoutData.layout.length > 0 && (
          <GridLayout
            cols={numberOfColumns}
            rowHeight={320}
            width={containerWidth}
            margin={[spacingBetweenWidgets, spacingBetweenWidgets]}
            containerPadding={[spacingBetweenWidgets, spacingBetweenWidgets]}
            compactType="horizontal"
            useCSSTransforms={true}
            layout={layoutData.layout}
            onDragStop={handleDragStop}
            onResizeStop={handleResizeStop}
            isDraggable={movable}
            isDroppable={movable}
            isResizable={resizable}
          >
            {layoutData.layout.map(({ i, w }) => {
              const widgetSettings = layoutData.settings[i];
              const widget = myWorkspaceWidgetsMap[widgetSettings.widget];
              const isAvailable = isWidgetAvailable(widgetSettings);

              if (!widget) {
                throw new Error(
                  `Widget "${widgetSettings.widget}" is not registered!`,
                );
              }

              return (
                <Box key={widgetSettings.id} sx={conditionalSx.widgetContainer}>
                  {manageWorkspace && (
                    <ManageWidgetMenu settings={widgetSettings} />
                  )}

                  <Box
                    sx={conditionalSx.widget}
                    className={!isAvailable ? 'unavailable' : undefined}
                  >
                    {React.createElement(
                      isAvailable ? widget.component : memo(PlaceholderWidget),
                      {
                        title: widgetSettings.title,
                        config: {
                          ...widgetSettings.config,
                          workspaceType: activeWorkspace.type as WorkspaceType,
                        },
                        width: Math.round(
                          ((containerWidth -
                            (numberOfColumns + 1) * spacingBetweenWidgets) /
                            numberOfColumns) *
                            w +
                            (w - 1) * spacingBetweenWidgets,
                        ),
                      },
                    )}
                  </Box>
                  {!isAvailable && (
                    <Box sx={sxStyles.unavailableWidget}>
                      <Box sx={sxStyles.unavailableWidgetMessage}>
                        {t('widgetUnavailable')}
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            })}
          </GridLayout>
        )) || (
          <Typography sx={sxStyles.emptyWorkspace}>
            {hasNoWorkspace ? (
              <AddWorkspaceButton workspaces={workspaces} />
            ) : (
              t('workspaceIsEmpty')
            )}
          </Typography>
        ))}

      {manageWorkspace && <AddWidgetMenu workspaceSettings={manageWorkspace} />}
    </Box>
  );
};
